import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DOCTOR_HOW_IT_WORKS_PAGES } from '@medicusunion/front-utils';
import {
  DeleteOutlined,
  EyeOutlined,
  ShareAltOutlined
} from '@ant-design/icons';
import { App, Button, Flex, Image, Modal, Table, Typography } from 'antd';
import { useAppDispatch } from 'store/hooks';
import { setTour } from 'store/tour/slice';
import { ColumnProps } from 'antd/lib/table';
import DeleteIcon from 'assets/icons/delete.svg';
import {
  DicomShareRow,
  deleteDicomShare,
  fetchDicomShares
} from 'services/api/dicomShares';

import {
  ConfirmModalContent,
  HowItWorksLink
  // Password
} from 'components/shared';
import { CreateDicom } from './components';
import { PageWrapper } from 'components/ui';
import { showRestError } from 'utils/showErrorMessage';
import useCopyText from 'hooks/useCopyText';

const PAGE_LIMIT = 10;

const { Title } = Typography;

const Dicom: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { copyText } = useCopyText();

  const { message } = App.useApp();

  // const [isOpenModal, setIsOpenModal] = useState(false);
  const dispatch = useAppDispatch();

  const [results, setResults] = useState<DicomShareRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setDeleting] = useState(false);

  const load = useCallback(
    async (pageToLoad: number) => {
      setLoading(true);

      try {
        const res = await fetchDicomShares({
          page: pageToLoad,
          limit: PAGE_LIMIT
        });

        setResults(res.data);
        setTotal(res.total);
        dispatch(setTour({ key: 'dicom', value: true }));
      } catch (err) {
        showRestError(err);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    load(page);
  }, [load, page]);

  const onClickDelete = useCallback(
    (id: number) => {
      Modal.confirm({
        closable: !isDeleting,
        content: (
          <ConfirmModalContent
            icon={
              <Image preview={false} src={DeleteIcon} width={49} height={49} />
            }
            title={t('dicom.delete_dicom')}
            message={t('dicom.are_you_sure')}
            onOk={() => {
              setDeleting(true);
              deleteDicomShare(id)
                .then(() => {
                  load(page);
                  Modal.destroyAll();
                })
                .catch(err => showRestError(err))
                .finally(() => setDeleting(false));
            }}
            onCancel={() => Modal.destroyAll()}
          />
        ),
        icon: null,
        footer: null
      });
    },
    [isDeleting, load, page, t]
  );

  const onClickShare = useCallback(
    (link: string) => {
      copyText(link);
      message.success(t('messages.copied'));
    },
    [copyText, message, t]
  );

  const columns: ColumnProps<DicomShareRow>[] = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: t('sessions.title'),
        dataIndex: 'name',
        key: 'name',
        render(value) {
          return value || '-';
        }
      },
      {
        title: t('sessions.file'),
        dataIndex: 'file',
        key: 'file'
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'actions',
        align: 'right',
        render: (id, record) => {
          const link = `${record.redirect_url}?sessionId=${record.session_id}`;

          return (
            <Flex align="center" justify="flex-end" gap={16}>
              <Button
                icon={<DeleteOutlined />}
                type="text"
                onClick={() => onClickDelete(id)}
                danger
              >
                {t('table.delete')}
              </Button>
              <Button
                icon={<ShareAltOutlined />}
                onClick={() => onClickShare(link)}
                // onClick={() => setIsOpenModal(true)}
              >
                {t('dicom.share')}
              </Button>
              <a target="_blank" href={link} rel="noreferrer">
                <Button icon={<EyeOutlined />} type="primary" ghost>
                  {t('dicom.view_dicom')}
                </Button>
              </a>
            </Flex>
          );
        }
      }
    ],
    [onClickDelete, onClickShare, t]
  );

  return (
    <>
      <PageWrapper
        title="DICOM"
        color="white"
        extra={[
          <HowItWorksLink
            key="how-it-works"
            type={DOCTOR_HOW_IT_WORKS_PAGES.DICOM.key}
            title={t('common.how_it_works')}
          />
        ]}
      >
        <CreateDicom onCreated={() => load(page)} />
        <Title level={3}>{t('dicom.previous_dicoms')}</Title>
        {/* <Space size={16} style={{ marginBottom: 16 }}>
      <Input.Search
        style={{ width: 289 }}
        placeholder={t('dicom.search_by_file')}
        size="large"
      />
      <Space size={8}>
        <Button type="primary" size="large">
          {t('common.apply')}
        </Button>
        <Button type="default" size="large">
          {t('common.reset')}
        </Button>
      </Space>
    </Space> */}
        <Table
          dataSource={results}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            total,
            current: page,
            pageSize: PAGE_LIMIT,
            onChange: p => setPage(p),
            showSizeChanger: false
          }}
          scroll={{ x: 768 }}
        />
      </PageWrapper>
      {/* {isOpenModal && <ShareDicomModal onClose={() => setIsOpenModal(false)} />} */}
    </>
  );
};

export default Dicom;
